import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSpring, animated } from "react-spring";
import { Background, ModalContent, CloseModalButton, Form, FormContent, Button, Input, Label } from "../../ModalRegisterStyle";
import Swal from "sweetalert2";
import api from "../../../../services/api";

export default function RegisterManual({ showModal, setShowModal, onSaved }) {
  const [matricula, setMatricula] = useState("");
  const [colaborador, setColaborador] = useState("");
  const [info, setInfo] = useState(null);
  const [cesta, setCesta] = useState(false);
  const [brinquedo, setBrinquedo] = useState(false);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef();
  const animation = useSpring({
    config: {
      duration: 150,
    },
    opacity: showModal ? 1 : 0,
    transform: showModal ? `translateY(0%)` : `translateY(-100%)`,
  });

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  });

  const keyPress = useCallback(
    (e) => {
      if (e.key === "Escape" && showModal) setShowModal(false);
    },
    [setShowModal, showModal]
  );

  useEffect(() => {
    document.addEventListener("keydown", keyPress);
    return () => document.removeEventListener("keydown", keyPress);
  }, [keyPress]);

  const buscarColaboradorPorMatricula = async (mat) => {
    if (!mat) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/rh/controle-cestas/buscar-colaborador/${mat}`);
      if (data.error) {
        Swal.fire("Atenção!", data.error, "warning");
        setMatricula("");
        setColaborador("");
        setInfo(null);
        return;
      }
      const item = data.item;
      setInfo(item);
      setColaborador(item.nome_colaborador || "");
      if (item.cesta_natal === 1 && !item.data_retirada_cesta) {
        setCesta(true);
        setBrinquedo(false);
      }
      if (item.brinquedo > 0 && !item.data_retirada_brinquedo) {
        setBrinquedo(true);
        setCesta(false);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.error || "Não foi possível consultar os dados!";
      Swal.fire("Atenção!", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!cesta && !brinquedo) {
      Swal.fire("Atenção!", "Selecione Cesta ou Brinquedo.", "warning");
      return;
    }
    setLoading(true);
    try {
      const dataEnviar = {
        matricula: matricula,
        tipo: cesta ? "cesta" : "brinquedo",
      };
      const { data } = await api.put(`/rh/controle-cestas/atualizar-manual`, { dataEnviar });
      if (data.error) {
        Swal.fire("Atenção!", data.error, "warning");
      } else {
        await Toast.fire({ icon: "success", title: data.success || "Operação realizada com sucesso!" });
        onSaved && onSaved();
        setShowModal(false);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.error || "Ocorreu um erro ao registrar a retirada.";
      Swal.fire("Atenção!", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {showModal ? (
        <Background
          ref={modalRef}
          onClick={(e) => {
            if (modalRef.current === e.target) {
              setShowModal(false);
            }
          }}
        >
          <animated.div style={animation}>
            <ModalContent>
              <br />
              <Form onSubmit={handleSubmit}>
                <h3>Retirada manual</h3>
                <FormContent style={{ alignItems: "start" }}>
                  <Label>Matrícula:</Label>
                  <Input
                    style={{ width: "100%" }}
                    value={matricula}
                    disabled={loading}
                    onChange={(e) => setMatricula(e.target.value)}
                    onBlur={(e) => buscarColaboradorPorMatricula(e.target.value)}
                  />
                  <Label>Nome:</Label>
                  <Input style={{ width: "100%" }} value={colaborador} disabled />

                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={cesta}
                        disabled={loading || !info?.cesta_natal || !!info?.data_retirada_cesta}
                        onChange={(e) => {
                          setCesta(e.target.checked);
                          if (e.target.checked) setBrinquedo(false);
                        }}
                      />
                      Cesta
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={brinquedo}
                        disabled={
                          loading || (!info?.brinquedo_1 && !info?.brinquedo_2 && !info?.brinquedo_3) || !!info?.data_retirada_brinquedo
                        }
                        onChange={(e) => {
                          setBrinquedo(e.target.checked);
                          if (e.target.checked) setCesta(false);
                        }}
                      />
                      Brinquedo
                    </label>
                  </div>

                  {brinquedo && info && (
                    <div style={{ marginTop: 12, textAlign: "left" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <tbody>
                          {["brinquedo_1", "brinquedo_2", "brinquedo_3"].map((key) => {
                            const val = info[key];
                            if (!val) return null;
                            return (
                              <tr key={key}>
                                <td style={{ padding: "4px 8px", borderBottom: "1px solid #f1f1f1" }}>{val}</td>
                              </tr>
                            );
                          })}
                          {!(info.brinquedo_1 || info.brinquedo_2 || info.brinquedo_3) && (
                            <tr>
                              <td style={{ padding: "6px 8px" }}>Nenhum brinquedo disponível</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </FormContent>
                <br />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button type="submit" disabled={loading}>
                    {loading ? "Salvando..." : "Salvar Retirada"}
                  </Button>
                  <Button type="button" onClick={() => setShowModal(false)} style={{ background: "#e8343d" }}>
                    Fechar
                  </Button>
                </div>
              </Form>
              <CloseModalButton
                onClick={() => {
                  setShowModal(false);
                }}
              />
            </ModalContent>
          </animated.div>
        </Background>
      ) : null}
    </>
  );
}
